import React from "react";
import { type TicketListItem } from "../../../../api/tickets";
import TicketRow from "./TicketRow";

export type SortKey =
  | "id"
  | "status"
  | "severity_opened"
  | "site_id"
  | "array_inverter_id"
  | "transformer"
  | "opened_at";
export type SortDirection = "asc" | "desc";
export type SortState = { key: SortKey; direction: SortDirection };

export type SiteMeta = {
  siteName?: string;
  transformers: Record<number, string>;
  arrays: Record<number, { name?: string; transformerId?: number | null }>;
  arrayInverters: Record<number, { name?: string; arrayId?: number | null; inverterId?: number | null }>;
  inverters: Record<number, string>;
};

export type TicketDisplay = {
  siteName: string;
  transformerName?: string;
  inverterName?: string;
};

type Props = {
  tickets: TicketListItem[];
  loading?: boolean;
  sort: SortState;
  onSort: (next: SortState) => void;
  displayById?: Record<number, TicketDisplay>;
};

const sortOptions: { key: SortKey; label: string }[] = [
  { key: "id", label: "ID" },
  { key: "status", label: "Status" },
  { key: "severity_opened", label: "Severity" },
  { key: "site_id", label: "Site" },
  { key: "transformer", label: "Transformer" },
  { key: "array_inverter_id", label: "Inverter" },
  { key: "opened_at", label: "Opened" },
];

export default function TicketTable({ tickets, loading, sort, onSort, displayById }: Props) {
  const handleSortKey = (key: SortKey) => {
    onSort({ key, direction: sort.key === key ? sort.direction : "desc" });
  };

  const toggleDirection = () => {
    onSort({ ...sort, direction: sort.direction === "asc" ? "desc" : "asc" });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-100">
        <div>
          <p className="text-sm font-semibold text-slate-900">Tickets</p>
          <p className="text-xs text-slate-500">
            {loading ? "Loading..." : `${tickets.length} ticket${tickets.length === 1 ? "" : "s"}`}
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-slate-600">
          <span className="font-semibold">Sort</span>
          <select
            className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700"
            value={sort.key}
            onChange={(e) => handleSortKey(e.target.value as SortKey)}
          >
            {sortOptions.map((option) => (
              <option key={option.key} value={option.key}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={toggleDirection}
            className="inline-flex items-center justify-center rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
            aria-label={`Toggle sort direction (currently ${sort.direction})`}
          >
            <svg className="h-3 w-3" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5">
              {sort.direction === "asc" ? (
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 5l-4 5h8l-4-5z" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 15l4-5H6l4 5z" />
              )}
            </svg>
          </button>
        </div>
      </div>

      <ul className="divide-y divide-slate-100">
        {loading
          ? [...Array(5)].map((_, idx) => (
              <li key={idx} className="px-4 py-4">
                <div className="flex items-center gap-4 animate-pulse">
                  <div className="h-12 w-12 rounded-full bg-slate-100" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-40 rounded bg-slate-100" />
                    <div className="h-3 w-56 rounded bg-slate-100" />
                  </div>
                  <div className="hidden sm:block h-4 w-20 rounded bg-slate-100" />
                </div>
              </li>
            ))
          : tickets.length === 0
            ? (
              <li className="px-4 py-8 text-center text-sm text-slate-500">No tickets found for the selected filters.</li>
            )
            : tickets.map((ticket) => (
                <TicketRow
                  key={ticket.id}
                  ticket={ticket}
                  display={displayById?.[ticket.id]}
                />
              ))}
      </ul>
    </div>
  );
}
